import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import type {
  ParentState,
  HealthItem,
  ReactionEmoji,
  CommentReply,
  Comment,
  HistoryEntry,
  SprintHistoryEntry,
} from "./sharedTypes";
import {
  STORY_HEALTH_DEFINITIONS,
  computeStoryHealthItems,
  toggleStoryHealthAcknowledge,
  addStoryComment,
  updateStoryComment,
  deleteStoryComment,
  toggleStoryCommentReaction,
  addStoryCommentReply,
  updateStoryCommentReply,
  deleteStoryCommentReply,
} from "./storiesSlice";
import type { StoryType, Story } from "./storiesSlice";

export interface EpicCapacity {
  Engineering: number;
  practices?: Record<string, number>;
}

/** Health issue definitions checked against an Epic. */
export const EPIC_HEALTH_DEFINITIONS: Array<{
  id: string;
  reason: string;
  score: number;
}> = [
  { id: "no_description", reason: "Epic has no description", score: 20 },
  {
    id: "no_stories",
    reason: "No stories have been added to this epic",
    score: 15,
  },
  {
    id: "score_overridden",
    reason: "Story point total has been manually overridden",
    score: 5,
  },
  {
    id: "no_workstream",
    reason: "Epic has no workstream assigned",
    score: 15,
  },
];

export interface Epic {
  id: string;
  title: string;
  description: string;
  categoryId: string | null;
  /** Optional pod assignment — pods are now managed through Workstream entries. */
  podId?: string | null;
  priority: number;
  capacity: EpicCapacity;
  stories?: Story[];
  storyPointOverrides?: Partial<Record<StoryType, number>>;
  /** High-level lifecycle stage: Open, In Progress, or Closed. */
  parentState?: ParentState;
  /** Configurable child status within the parentState (e.g. "Blocked", "Ready"). */
  status?: string;
  /** The workstream this epic is assigned to (primary assignment). */
  workstreamId: string | null;
  /** Map of health item ID → acknowledged flag for this epic. */
  acknowledgedHealthItems?: Record<string, boolean>;
  /** Comments posted on this epic. */
  comments?: Comment[];
  /** Saved change history for this epic (newest first). */
  history?: HistoryEntry[];
}

// ---------------------------------------------------------------------------
// Sprints
// ---------------------------------------------------------------------------

/** High-level lifecycle stage of a sprint. */
export type SprintStatus = "Planning" | "Active" | "Completed" | "Cancelled";

/**
 * Represents a single sprint used to organise and move stories through
 * their workflow states.  This is distinct from the sprint-forecasting
 * configuration (sprintsSlice) which drives capacity planning; this
 * record describes an actual team sprint with dates, goals, and outcomes.
 *
 * Pod associations are intentionally absent from Sprint — they are managed
 * through SprintBoard entries so that the same sprint record can be shared
 * across multiple boards or assigned to different subsets of pods per board.
 *
 * NOTE: Modelled for future Redux state integration.
 */
export interface Sprint {
  /** Unique identifier for this sprint. */
  id: string;
  /** Human-readable sprint name (e.g. "Sprint 3" or "Q2 Sprint 1"). */
  name: string;
  /** The primary objective the team committed to for this sprint. */
  goal: string;
  /** ISO date string for the first day of the sprint. */
  startDate: string;
  /** ISO date string for the last day of the sprint (inclusive). */
  endDate: string;
  /** Current lifecycle stage of this sprint. */
  status: SprintStatus;
  /** Total story points committed to at sprint planning. */
  plannedCapacity?: number;
  /** Total story points actually completed by the sprint close date. */
  completedCapacity?: number;
  /** General notes, daily stand-up observations, or mid-sprint updates. */
  notes?: string;
  /** Retrospective summary captured at the end of the sprint. */
  retrospective?: string;
  /** ISO date string of when this sprint record was created. */
  createdAt: string;
  /** ISO date string of the last time this sprint record was modified. */
  updatedAt?: string;
  /**
   * Audit trail for this sprint (newest first).
   * Captures creation, status transitions, and any field-level edits.
   */
  history?: SprintHistoryEntry[];
}

// ---------------------------------------------------------------------------

export const getEffectiveStoryPoints = (
  epic: Epic,
  type: StoryType,
): number => {
  if (epic.storyPointOverrides?.[type] !== undefined) {
    return epic.storyPointOverrides[type] as number;
  }
  return (epic.stories ?? [])
    .filter((s) => s.type === type)
    .reduce((sum, s) => sum + s.points, 0);
};

/** Compute the active health items for an epic based on its current state.
 *  Optional `epicWeights` overrides the default scores from EPIC_HEALTH_DEFINITIONS. */
export const computeEpicHealthItems = (
  epic: Epic,
  epicWeights?: Record<string, number>,
): HealthItem[] => {
  const acked = epic.acknowledgedHealthItems ?? {};
  const defMap = new Map(
    EPIC_HEALTH_DEFINITIONS.map((d) => [
      d.id,
      { ...d, score: epicWeights?.[d.id] ?? d.score },
    ]),
  );
  const push = (id: string): void => {
    const def = defMap.get(id);
    if (def) {
      items.push({ ...def, acknowledged: !!acked[id] });
    }
  };
  const items: HealthItem[] = [];

  if (!epic.description?.trim()) push("no_description");
  if (!epic.stories || epic.stories.length === 0) push("no_stories");
  if (epic.storyPointOverrides?.["Engineering"] !== undefined)
    push("score_overridden");
  if (epic.workstreamId === null) push("no_workstream");

  return items;
};

/**
 * Sum of scores from unacknowledged epic health items.
 * Higher score = less healthy.
 */
export const getEpicHealthScore = (
  epic: Epic,
  epicWeights?: Record<string, number>,
): number =>
  computeEpicHealthItems(epic, epicWeights)
    .filter((item) => !item.acknowledged)
    .reduce((sum, item) => sum + item.score, 0);

// Default maximum possible penalty totals derived from the definition lists.
const EPIC_MAX_PENALTY = EPIC_HEALTH_DEFINITIONS.reduce(
  (s, d) => s + d.score,
  0,
); // e.g. 40
// Story penalties are normalised so that epic + story components sum to 100 max.
const STORY_NORMALIZED_MAX = 100 - EPIC_MAX_PENALTY; // e.g. 35

/**
 * Returns a 0-100 health percentage for a single epic (higher = healthier).
 *
 * Formula:
 *   - Epic-level unacknowledged penalty (0–epicMaxPenalty)
 *   - Story-level: average unacknowledged penalty per story, normalised to
 *     0–storyNormalizedMax so the two components together span 0–100.
 *   - health = 100 – (epicPenalty + storyNormalised), clamped to [0, 100]
 *
 * Optional `epicWeights` / `storyWeights` override the default definition scores.
 */
export const getEpicHealthPercent = (
  epic: Epic,
  epicWeights?: Record<string, number>,
  storyWeights?: Record<string, number>,
): number => {
  const epicMaxPenalty = EPIC_HEALTH_DEFINITIONS.reduce(
    (s, d) => s + (epicWeights?.[d.id] ?? d.score),
    0,
  );
  const storyMaxPenalty = STORY_HEALTH_DEFINITIONS.filter(
    (d) => d.id !== "no_epic",
  ).reduce((s, d) => s + (storyWeights?.[d.id] ?? d.score), 0);
  const storyNormalizedMax =
    epicMaxPenalty > 0
      ? Math.max(0, 100 - epicMaxPenalty)
      : STORY_NORMALIZED_MAX;

  const epicPenalty = computeEpicHealthItems(epic, epicWeights)
    .filter((i) => !i.acknowledged)
    .reduce((sum, i) => sum + i.score, 0);

  const stories = epic.stories ?? [];
  let storyNormalized = 0;
  if (stories.length > 0 && storyMaxPenalty > 0) {
    const totalStoryPenalty = stories.reduce((sum, story) => {
      return (
        sum +
        computeStoryHealthItems(story, storyWeights)
          .filter((i) => !i.acknowledged)
          .reduce((s2, i) => s2 + i.score, 0)
      );
    }, 0);
    const avgStoryPenalty = totalStoryPenalty / stories.length;
    storyNormalized = (avgStoryPenalty / storyMaxPenalty) * storyNormalizedMax;
  }

  const totalPenalty = epicPenalty + storyNormalized;
  return Math.max(0, Math.min(100, Math.round(100 - totalPenalty)));
};

/**
 * Returns a 0-100 overall backlog health percentage averaged across all epics.
 * Returns 100 when there are no epics (empty backlog).
 *
 * Optional `epicWeights` / `storyWeights` override the default definition scores.
 */
export const getBacklogHealthPercent = (
  epics: Epic[],
  epicWeights?: Record<string, number>,
  storyWeights?: Record<string, number>,
): number => {
  if (epics.length === 0) return 100;
  const total = epics.reduce(
    (sum, e) => sum + getEpicHealthPercent(e, epicWeights, storyWeights),
    0,
  );
  return Math.round(total / epics.length);
};

interface EpicsState {
  epics: Epic[];
}

const initialState: EpicsState = {
  epics: [],
};

const epicsSlice = createSlice({
  name: "epics",
  initialState,
  reducers: {
    addEpic: (state, action: PayloadAction<Omit<Epic, "id">>) => {
      state.epics.push({ ...action.payload, id: uuidv4() });
    },
    updateEpic: (state, action: PayloadAction<Epic>) => {
      const idx = state.epics.findIndex((e) => e.id === action.payload.id);
      if (idx !== -1) state.epics[idx] = action.payload;
    },
    deleteEpic: (state, action: PayloadAction<string>) => {
      state.epics = state.epics.filter((e) => e.id !== action.payload);
    },
    reorderEpics: (
      state,
      action: PayloadAction<{ workstreamId: string | null; epics: Epic[] }>,
    ) => {
      const others = state.epics.filter(
        (e) => e.workstreamId !== action.payload.workstreamId,
      );
      state.epics = [...others, ...action.payload.epics];
    },
    moveEpic: (
      state,
      action: PayloadAction<{
        epicId: string;
        toWorkstreamId: string | null;
        newPriority: number;
      }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        epic.workstreamId = action.payload.toWorkstreamId;
        epic.priority = action.payload.newPriority;
      }
    },
    removeCategoryFromEpics: (state, action: PayloadAction<string>) => {
      state.epics.forEach((e) => {
        if (e.categoryId === action.payload) e.categoryId = null;
      });
    },
    setEpics: (state, action: PayloadAction<Epic[]>) => {
      state.epics = action.payload;
    },
    reorderAllEpics: (state, action: PayloadAction<string[]>) => {
      const epicMap = new Map(state.epics.map((e) => [e.id, e]));
      const reordered = action.payload
        .map((id) => epicMap.get(id))
        .filter((e): e is Epic => e !== undefined);
      const missing = state.epics.filter((e) => !action.payload.includes(e.id));
      state.epics = [...reordered, ...missing];
    },
    /** Toggle the acknowledged flag for an epic-level health item. */
    toggleEpicHealthAcknowledge: (
      state,
      action: PayloadAction<{ epicId: string; healthItemId: string }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        const acked = epic.acknowledgedHealthItems ?? {};
        epic.acknowledgedHealthItems = {
          ...acked,
          [action.payload.healthItemId]: !acked[action.payload.healthItemId],
        };
      }
    },

    // ── Epic Comments ────────────────────────────────────────────────────────

    addEpicComment: (
      state,
      action: PayloadAction<{
        epicId: string;
        comment: Omit<Comment, "id" | "createdAt" | "reactions" | "replies">;
      }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        if (!epic.comments) epic.comments = [];
        epic.comments.push({
          id: uuidv4(),
          ...action.payload.comment,
          createdAt: new Date().toISOString(),
          reactions: [],
          replies: [],
        });
      }
    },
    updateEpicComment: (
      state,
      action: PayloadAction<{
        epicId: string;
        commentId: string;
        content: string;
      }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        const comment = (epic.comments ?? []).find(
          (c) => c.id === action.payload.commentId,
        );
        if (comment) {
          comment.content = action.payload.content;
          comment.updatedAt = new Date().toISOString();
        }
      }
    },
    deleteEpicComment: (
      state,
      action: PayloadAction<{ epicId: string; commentId: string }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic && epic.comments) {
        epic.comments = epic.comments.filter(
          (c) => c.id !== action.payload.commentId,
        );
      }
    },
    toggleEpicCommentReaction: (
      state,
      action: PayloadAction<{
        epicId: string;
        commentId: string;
        emoji: ReactionEmoji;
        userId: string;
      }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        const comment = (epic.comments ?? []).find(
          (c) => c.id === action.payload.commentId,
        );
        if (comment) {
          const reaction = comment.reactions.find(
            (r) => r.emoji === action.payload.emoji,
          );
          if (!reaction) {
            comment.reactions.push({
              emoji: action.payload.emoji,
              userIds: [action.payload.userId],
            });
          } else {
            const idx = reaction.userIds.indexOf(action.payload.userId);
            if (idx === -1) {
              reaction.userIds.push(action.payload.userId);
            } else {
              reaction.userIds.splice(idx, 1);
              if (reaction.userIds.length === 0) {
                comment.reactions = comment.reactions.filter(
                  (r) => r.emoji !== action.payload.emoji,
                );
              }
            }
          }
        }
      }
    },
    addEpicCommentReply: (
      state,
      action: PayloadAction<{
        epicId: string;
        commentId: string;
        reply: Omit<CommentReply, "id" | "createdAt">;
      }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        const comment = (epic.comments ?? []).find(
          (c) => c.id === action.payload.commentId,
        );
        if (comment) {
          comment.replies.push({
            id: uuidv4(),
            ...action.payload.reply,
            createdAt: new Date().toISOString(),
          });
        }
      }
    },
    updateEpicCommentReply: (
      state,
      action: PayloadAction<{
        epicId: string;
        commentId: string;
        replyId: string;
        content: string;
      }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        const comment = (epic.comments ?? []).find(
          (c) => c.id === action.payload.commentId,
        );
        if (comment) {
          const reply = comment.replies.find(
            (r) => r.id === action.payload.replyId,
          );
          if (reply) {
            reply.content = action.payload.content;
            reply.updatedAt = new Date().toISOString();
          }
        }
      }
    },
    deleteEpicCommentReply: (
      state,
      action: PayloadAction<{
        epicId: string;
        commentId: string;
        replyId: string;
      }>,
    ) => {
      const epic = state.epics.find((e) => e.id === action.payload.epicId);
      if (epic) {
        const comment = (epic.comments ?? []).find(
          (c) => c.id === action.payload.commentId,
        );
        if (comment) {
          comment.replies = comment.replies.filter(
            (r) => r.id !== action.payload.replyId,
          );
        }
      }
    },

    // ── Story Comments ───────────────────────────────────────────────────────
    // Handled via extraReducers below (actions owned by storiesSlice).
  },
  extraReducers: (builder) => {
    builder
      .addCase(toggleStoryHealthAcknowledge, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story) {
            const acked = story.acknowledgedHealthItems ?? {};
            story.acknowledgedHealthItems = {
              ...acked,
              [action.payload.healthItemId]:
                !acked[action.payload.healthItemId],
            };
          }
        }
      })
      .addCase(addStoryComment, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story) {
            if (!story.comments) story.comments = [];
            story.comments.push({
              id: uuidv4(),
              ...action.payload.comment,
              createdAt: new Date().toISOString(),
              reactions: [],
              replies: [],
            });
          }
        }
      })
      .addCase(updateStoryComment, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story) {
            const comment = (story.comments ?? []).find(
              (c) => c.id === action.payload.commentId,
            );
            if (comment) {
              comment.content = action.payload.content;
              comment.updatedAt = new Date().toISOString();
            }
          }
        }
      })
      .addCase(deleteStoryComment, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story && story.comments) {
            story.comments = story.comments.filter(
              (c) => c.id !== action.payload.commentId,
            );
          }
        }
      })
      .addCase(toggleStoryCommentReaction, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story) {
            const comment = (story.comments ?? []).find(
              (c) => c.id === action.payload.commentId,
            );
            if (comment) {
              const reaction = comment.reactions.find(
                (r) => r.emoji === action.payload.emoji,
              );
              if (!reaction) {
                comment.reactions.push({
                  emoji: action.payload.emoji,
                  userIds: [action.payload.userId],
                });
              } else {
                const idx = reaction.userIds.indexOf(action.payload.userId);
                if (idx === -1) {
                  reaction.userIds.push(action.payload.userId);
                } else {
                  reaction.userIds.splice(idx, 1);
                  if (reaction.userIds.length === 0) {
                    comment.reactions = comment.reactions.filter(
                      (r) => r.emoji !== action.payload.emoji,
                    );
                  }
                }
              }
            }
          }
        }
      })
      .addCase(addStoryCommentReply, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story) {
            const comment = (story.comments ?? []).find(
              (c) => c.id === action.payload.commentId,
            );
            if (comment) {
              comment.replies.push({
                id: uuidv4(),
                ...action.payload.reply,
                createdAt: new Date().toISOString(),
              });
            }
          }
        }
      })
      .addCase(updateStoryCommentReply, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story) {
            const comment = (story.comments ?? []).find(
              (c) => c.id === action.payload.commentId,
            );
            if (comment) {
              const reply = comment.replies.find(
                (r) => r.id === action.payload.replyId,
              );
              if (reply) {
                reply.content = action.payload.content;
                reply.updatedAt = new Date().toISOString();
              }
            }
          }
        }
      })
      .addCase(deleteStoryCommentReply, (state, action) => {
        const epic = state.epics.find((e) => e.id === action.payload.epicId);
        if (epic) {
          const story = (epic.stories ?? []).find(
            (s) => s.id === action.payload.storyId,
          );
          if (story) {
            const comment = (story.comments ?? []).find(
              (c) => c.id === action.payload.commentId,
            );
            if (comment) {
              comment.replies = comment.replies.filter(
                (r) => r.id !== action.payload.replyId,
              );
            }
          }
        }
      });
  },
});

export const {
  addEpic,
  updateEpic,
  deleteEpic,
  reorderEpics,
  moveEpic,
  removeCategoryFromEpics,
  setEpics,
  reorderAllEpics,
  toggleEpicHealthAcknowledge,
  addEpicComment,
  updateEpicComment,
  deleteEpicComment,
  toggleEpicCommentReaction,
  addEpicCommentReply,
  updateEpicCommentReply,
  deleteEpicCommentReply,
} = epicsSlice.actions;

export default epicsSlice.reducer;
